import React from 'react';
import { ArticleEditor } from '../ui/ArticleEditor';
import { usePressRelease, useCreatePressRelease, useUpdatePressRelease } from '../../lib/api/hooks/press-release';
import { PressReleaseFormSchema } from '../../lib/api/types';
import { showToast } from '../../lib/toast';

interface PressReleaseEditorProps {
  articleId?: number | string;
  onBack?: () => void;
  onSuccess?: () => void;
}

const PressReleaseEditor: React.FC<PressReleaseEditorProps> = ({ articleId, onBack, onSuccess }) => {
  const { data: pressReleaseData, isLoading: loadingPressRelease } = usePressRelease(
    String(articleId),
  );

  const { mutate: createPressRelease, isPending: isCreating } = useCreatePressRelease();
  const { mutate: updatePressRelease, isPending: isUpdating } = useUpdatePressRelease();

  const isEditMode = !!articleId;
  const isSaving = isCreating || isUpdating;

  // Define highlight options specific to Press Releases
  const highlightOptions = [
    { key: 'landingPage', label: 'Landing page/Hero section' },
    { key: 'pressRelease', label: 'Press Release', defaultValue: true },
    { key: 'pressReleaseBanner', label: 'Press Release/Banner' }
  ];

  const handleSubmit = (data: any, saveStatus: 'DRAFT' | 'PUBLISH') => {
    // Validate cover image
    if (!data.coverImage) {
      showToast.error('Please upload a cover image');
      return;
    }

    // Convert articleTag to array
    const tagsArray = typeof data.articleTag === 'string'
      ? data.articleTag.split(',').map((t: string) => t.trim()).filter(Boolean)
      : Array.isArray(data.articleTag)
      ? data.articleTag
      : [];

    const formData = {
      title: data.title,
      description: data.description || '',
      content: data.content,
      coverImageTitle: data.coverImageTitle || '',
      articleTag: tagsArray,
      landingPage: data.landingPage,
      pressRelease: data.pressRelease,
      pressReleaseBanner: data.pressReleaseBanner,
      allowComment: data.allowComment,
      status: saveStatus,
      coverImageUrl: data.coverImage
    };

    if (isEditMode) {
      updatePressRelease(
        { pressReleaseId: articleId as string, data: formData },
        {
          onSuccess: () => {
            showToast.success(
              saveStatus === 'PUBLISH'
                ? 'Press release published successfully!'
                : 'Press release updated successfully!'
            );
            onSuccess?.();
          },
          onError: (error) => {
            showToast.error('Failed to update: ' + (error.message || 'Unknown error'));
          }
        }
      );
    } else {
      createPressRelease(formData, {
        onSuccess: () => {
          showToast.success(
            saveStatus === 'PUBLISH'
              ? 'Press release published successfully!'
              : 'Press release created as draft!'
          );
          onSuccess?.();
        },
        onError: (error) => {
          showToast.error('Failed to create: ' + (error.message || 'Unknown error'));
        }
      });
    }
  };

  return (
    <ArticleEditor
      formSchema={PressReleaseFormSchema}
      articleId={articleId}
      articleData={pressReleaseData?.data}
      isLoading={loadingPressRelease}
      onBack={onBack}
      onSubmit={handleSubmit}
      highlightOptions={highlightOptions}
      isSaving={isSaving}
    />
  );
};

export default PressReleaseEditor;
